import { useState } from 'react'
import { adminClient, type RoleRow } from '@/auth/adminClient'
import { Modal, inputStyle, labelStyle, btnPrimary, btnGhost } from './modalShared'

export default function NewUserModal({
  roles,
  onClose,
  onCreated,
}: {
  roles: RoleRow[]
  onClose: () => void
  onCreated: () => void | Promise<void>
}) {
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [password, setPassword] = useState('')
  const [roleName, setRoleName] = useState<string>('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Only let admins assign platform-wide roles directly from this quick form.
  // Operator/organisation scoping is in the Manage Roles modal.
  const platformRoles = roles.filter((r) => r.is_system)

  async function submit() {
    setSubmitting(true)
    setError(null)
    try {
      await adminClient.createUser({
        email,
        display_name: displayName || email,
        password,
        roles: roleName
          ? [{ role: roleName, resource_type: null, resource_id: null }]
          : undefined,
      })
      await onCreated()
    } catch (e) {
      const status = (e as { status?: number })?.status
      const detail = (e as { detail?: { error?: string } })?.detail
      if (status === 409) setError('A user with that email already exists.')
      else if (detail?.error) setError(detail.error)
      else setError('Failed to create user.')
    } finally {
      setSubmitting(false)
    }
  }

  const canSubmit = email.length > 3 && password.length >= 8 && !submitting

  return (
    <Modal
      title="New user"
      onClose={onClose}
      footer={
        <>
          <button style={btnGhost} onClick={onClose}>Cancel</button>
          <button style={{ ...btnPrimary, opacity: canSubmit ? 1 : 0.6 }} disabled={!canSubmit} onClick={() => void submit()}>
            {submitting ? 'Creating…' : 'Create user'}
          </button>
        </>
      }
    >
      <label style={labelStyle}>Email</label>
      <input type="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} style={inputStyle} />

      <label style={labelStyle}>Display name</label>
      <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} style={inputStyle} />

      <label style={labelStyle}>Temporary password (min 8 chars)</label>
      <input
        type="text"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete="new-password"
        style={inputStyle}
      />

      <label style={labelStyle}>Platform role (optional)</label>
      <select value={roleName} onChange={(e) => setRoleName(e.target.value)} style={inputStyle}>
        <option value="">— No platform-wide role —</option>
        {platformRoles.map((r) => (
          <option key={r.id} value={r.name}>{r.name}</option>
        ))}
      </select>
      <div style={{ fontSize: 11, color: 'var(--t-3)', marginTop: 6 }}>
        Use the “Roles” action on a user row for operator- or organisation-scoped grants.
      </div>

      {error && (
        <div style={{ marginTop: 12, fontSize: 12, color: 'var(--crit)' }}>{error}</div>
      )}
    </Modal>
  )
}
